import { RavelElement } from '../../../base/ravel-element/src/RavelElement.js';
import globalStyles from '../../../../common/global-styles.js';
import  { RavelMessages }  from '../../../../../modules/RavelMessages.js';

// RavelCarousel.js
// Assumes you already have: RavelElement, globalStyles in scope/imported.

export class RavelCarousel extends RavelElement {
  static get localStyles() {
    return `
      <style>
        :host {
          display: block;
          --gap: 12px;
          --pad: 12px;
          --btn-size: 60px;
          --btn-bg: rgba(0,0,0,0.55);
          --btn-fg: white;
          --btn-radius: 999px;
          --fade-width: 28px;
          --snap: 1; /* set to 0 to disable scroll-snap */
          position: relative;
          box-sizing: border-box;
        }

        .wrap {
          position: relative;
          box-sizing: border-box;
          padding: var(--pad);
        }

        .viewport {
          position: relative;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
          scrollbar-gutter: stable both-edges;
          scroll-snap-type: x mandatory;
          /* allow horizontal wheel / trackpad */
          overscroll-behavior-x: contain;
          padding-bottom: 4px; /* room for scrollbar */
        }

        /* Turn off snap if --snap is 0 */
        .viewport[data-snap="0"] { scroll-snap-type: none; }

        /* The slotted items layout: flex row */
        ::slotted(*) {
          flex: 0 0 auto;
          scroll-snap-align: start;
        }

        .track {
          display: flex;
          gap: var(--gap);
          align-items: stretch;
          width: max-content;
          min-width: 100%;
          box-sizing: border-box;
        }

        /* Basic arrows */
        button.nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: var(--btn-size);
          height: var(--btn-size);
          border:none;
          font-size: 24px;
          /*
          border: 5px solid #ffff00;
          box-shadow: 0px 0px 0px 10px #ff0000;
          */
          border-radius: var(--btn-radius);
          background: var(--btn-bg);
          color: var(--btn-fg);
          cursor: pointer;
          display: grid;
          place-items: center;
          z-index: 2;
          padding: 0;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }

        button.nav[disabled] {
          opacity: 0.35;
          cursor: default;
        }

        button.prev { left: calc(var(--pad) * 0.5); }
        button.next { right: calc(var(--pad) * 0.5); }

        /* Subtle edge fades */
        .fade {
          position: absolute;
          top: 0;
          bottom: 0;
          width: var(--fade-width);
          pointer-events: none;
          z-index: 1;
        }
        .fade.left {
          left: var(--pad);
          background: linear-gradient(to right, rgba(0,0,0,0.25), rgba(0,0,0,0));
        }
        .fade.right {
          right: var(--pad);
          background: linear-gradient(to left, rgba(0,0,0,0.25), rgba(0,0,0,0));
        }

        /* Respect reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .viewport { scroll-behavior: auto; }
        }
      </style>
    `;
  }

  static get html() {
    return `
      <div class="wrap">
        <button class="nav prev" part="prev" aria-label="Scroll left" type="button">
          <span aria-hidden="true">‹</span>
        </button>

        <div class="fade left" part="fade-left"></div>
        <div class="fade right" part="fade-right"></div>

        <div class="viewport" part="viewport">
          <div class="track" part="track">
            <slot></slot>
          </div>
        </div>

        <button class="nav next" part="next" aria-label="Scroll right" type="button">
          <span aria-hidden="true">›</span>
        </button>
      </div>
    `;
  }

  static get observedAttributes() {
    return [
      ...super.baseObservedAttributes,
      // optional carousel controls:
      "page-size",  // "viewport" | number(px)
      "snap",       // "1" | "0"
      "arrows"      // "1" | "0"
    ];
  }

  constructor() {
    super();
    const template = document.createElement("template");
    template.innerHTML =
      globalStyles + this.constructor.localStyles + this.constructor.html;

    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(template.content.cloneNode(true));

    this.initialize();
  }

  connectedCallback() {
    this.setup();
  }

  disconnectedCallback() {
    this.teardown();
  }

  initialize() {
    this.rows = 1;
    this.cols = 1;

    this._onPrev = () => this.scrollByPage(-1);
    this._onNext = () => this.scrollByPage(1);
    this._onScroll = () => this.updateNavState();
    this._onSlotChange = () => this.updateNavState();
    this._ro = null;
  }

  setup = () => {
    this.$viewport = this.shadowRoot.querySelector(".viewport");
    this.$prev = this.shadowRoot.querySelector(".prev");
    this.$next = this.shadowRoot.querySelector(".next");
    this.$slot = this.shadowRoot.querySelector("slot");

    this.$prev.addEventListener("click", this._onPrev);
    this.$next.addEventListener("click", this._onNext);
    this.$viewport.addEventListener("scroll", this._onScroll, { passive: true });
    this.$slot.addEventListener("slotchange", this._onSlotChange);

    // Resize observer to keep disabled/enabled state accurate on layout changes
    if ("ResizeObserver" in window) {
      this._ro = new ResizeObserver(() => this.updateNavState());
      this._ro.observe(this.$viewport);
    } else {
      window.addEventListener("resize", this.updateNavState);
    }

    this.applyAttributesToUI();
    this.updateNavState();
  };

  teardown = () => {
    if (this.$prev) this.$prev.removeEventListener("click", this._onPrev);
    if (this.$next) this.$next.removeEventListener("click", this._onNext);
    if (this.$viewport) this.$viewport.removeEventListener("scroll", this._onScroll);
    if (this.$slot) this.$slot.removeEventListener("slotchange", this._onSlotChange);

    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    } else {
      window.removeEventListener("resize", this.updateNavState);
    }
  };

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue === newValue) return;

    if (name === "page-size" || name === "snap" || name === "arrows") {
      this.applyAttributesToUI();
      this.updateNavState();
    }
  }

  // ---------- Public-ish helpers ----------
  scrollByPage(direction = 1) {
    if (!this.$viewport) return;

    const pageSize = this.getPageSize();
    const left = this.$viewport.scrollLeft + direction * pageSize;
    this.$viewport.scrollTo({ left });
  }

  scrollToIndex(index = 0) {
    const items = this.getSlottedItems();
    const el = items[index];
    if (el) el.scrollIntoView({ inline: "start", block: "nearest" });
  }

  // ---------- Internals ----------
  getSlottedItems() {
    const assigned = this.$slot?.assignedElements?.({ flatten: true }) ?? [];
    return assigned.filter((n) => n && n.nodeType === 1);
  }

  getPageSize() {
    const attr = (this.getAttribute("page-size") || "viewport").trim();
    if (attr === "viewport") return this.$viewport.clientWidth;

    const n = Number(attr);
    if (Number.isFinite(n) && n > 0) return n;

    return this.$viewport.clientWidth;
  }

  applyAttributesToUI() {
    if (!this.$viewport) return;

    // snap
    const snap = this.getAttribute("snap");
    const snapOn = snap == null ? true : snap !== "0";
    this.$viewport.dataset.snap = snapOn ? "1" : "0";

    // arrows show/hide
    const arrows = this.getAttribute("arrows");
    const arrowsOn = arrows == null ? true : arrows !== "0";
    this.$prev.style.display = arrowsOn ? "" : "none";
    this.$next.style.display = arrowsOn ? "" : "none";
  }

  updateNavState = () => {
    if (!this.$viewport || !this.$prev || !this.$next) return;

    // If content doesn't overflow, disable both
    const maxScrollLeft = this.$viewport.scrollWidth - this.$viewport.clientWidth;

    // Tolerances help with subpixel rounding
    const tol = 1;

    const atStart = this.$viewport.scrollLeft <= tol;
    const atEnd = this.$viewport.scrollLeft >= maxScrollLeft - tol;
    const canScroll = maxScrollLeft > tol;

    this.$prev.disabled = !canScroll || atStart;
    this.$next.disabled = !canScroll || atEnd;

    // Optional: hide fades when not needed
    const leftFade = this.shadowRoot.querySelector(".fade.left");
    const rightFade = this.shadowRoot.querySelector(".fade.right");
    if (leftFade) leftFade.style.opacity = canScroll && !atStart ? "1" : "0";
    if (rightFade) rightFade.style.opacity = canScroll && !atEnd ? "1" : "0";
  };
}
